import type { HubConnection } from '@microsoft/signalr'
import {
  DetailedMessage,
  MessageThread,
} from 'Modules/Communication/Messages/shared/models/Message'
import {
  handleNewMessageNotificationThunk,
  handleNewThreadNotificationThunk,
} from 'Modules/Communication/Messages/shared/store/messaging/messages'
import { useAppDispatch } from 'shared/store/store'
import { getUserNotifications, negotiateSignalR } from 'shared/api/socket.api'
import { NOTIFICATION_RECEIVED } from 'shared/constants/broadcastChannels'
import useSignalRMessage from 'shared/hooks/useSignalRMessage'
import {
  ISocketMessage,
  mapApiSocketModel,
  SocketMessageTypeEnum,
} from 'shared/models/Socket'
import {
  setSocketMessageAsRead,
  socketStatusUpdate,
} from 'shared/store/socket/reducer'
import { isLocalhost } from 'shared/utils/isLocalhost'
import { isTestEnvironment } from 'shared/utils/isTestEnvironment'
import { convertObjectKeysToLowercase } from 'shared/utils/object-helpers'
import { useBroadcast } from './useBroadcast'

// Populated once signalR module is dynamically imported
let HubConnectionState: (typeof import('@microsoft/signalr'))['HubConnectionState']

const useSignalR = () => {
  const dispatch = useAppDispatch()
  const { customPayloadChecker, signalRMessageChecker, isBellNotification } =
    useSignalRMessage()
  const { send } = useBroadcast<boolean>(NOTIFICATION_RECEIVED, false)

  const initSignalR = async () => {
    try {
      const result = await negotiateSignalR()
      const signalR = await import('@microsoft/signalr')
      setupSignalRConnection(result.data.url, result.data.accessToken, signalR)
    } catch (error) {
      console.warn(error)
    }
  }

  const messagesRecieved = async (messages: ISocketMessage[]) => {
    for (const message of messages) {
      const CHECKED_IN_MESSAGE = signalRMessageChecker(message)
      // if (isTestEnvironment) console.debug('signalR message', message)
      if (isBellNotification(message.NotificationType)) send(true)

      if (typeof CHECKED_IN_MESSAGE === 'string') {
        const payload =
          customPayloadChecker(CHECKED_IN_MESSAGE, message) || message

        if (payload === null) return
        dispatch({
          type: CHECKED_IN_MESSAGE,
          payload,
        })
      } else if (CHECKED_IN_MESSAGE) {
        // Thunk type
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await dispatch((CHECKED_IN_MESSAGE as any)(message))
      }

      // Mark the message as read if it is not a bell notification
      if (shouldMarkNotificationAsRead(message)) {
        // Delaying read to prevent multiple tabs issue
        setTimeout(() => {
          dispatch(setSocketMessageAsRead(message.id))
        }, 2000)
      }
    }
  }

  const startSignalRConnection = async (connection: HubConnection) => {
    try {
      await connection.start()
      console.assert(connection.state === HubConnectionState.Connected)
      console.log('SignalR connection established')

      /*
id: string
  Notification: {
    Id: string
    NavneregisterId: string
    CorrelationId: string
    Role: string
    Type: string
    SenderSystem: string
    Payload: T
    SentDateTime: Date
    BblCode: string
  }
  NotificationType: SocketMessageTypeEnum
  ReceiverNavneregisterId: string
  ReceivedDateTime: Date
  ReadDateTime: Date
  DeletedDateTime: Date
  ReceivedByUserDateTime: Date
  InitialDelivery: boolean
      */
      const res = await getUserNotifications()
      const messages: ISocketMessage[] =
        res.data?.notifications?.map((n) => mapApiSocketModel(n)) || []
      await messagesRecieved(messages)
      // dispatch(socketStatusUpdate(connection.state))
    } catch (err) {
      console.assert(connection.state === HubConnectionState.Disconnected)
      console.warn('SignalR Connection Error: ', err)

      dispatch(socketStatusUpdate(connection.state))

      setTimeout(() => startSignalRConnection(connection), 5000)
    }
  }

  // Set up a SignalR connection to the specified hub URL
  const setupSignalRConnection = (
    connectionHub: string,
    accessToken: string,
    signalR: typeof import('@microsoft/signalr')
  ) => {
    const { HubConnectionBuilder, JsonHubProtocol, LogLevel } = signalR
    HubConnectionState = signalR.HubConnectionState
    const options = {
      logMessageContent: isLocalhost,
      logger: LogLevel.Warning,
      accessTokenFactory: () => accessToken,
    }

    // create the connection instance
    // withAutomaticReconnect will automatically try to reconnect
    // and generate a new socket connection if needed
    const connection = new HubConnectionBuilder()
      .withUrl(connectionHub, options)
      .withAutomaticReconnect()
      .withHubProtocol(new JsonHubProtocol())
      .configureLogging(LogLevel.Information)
      .build()

    const isDisconnected = connection.state === HubConnectionState.Disconnected
    const isReconnecting = connection.state === HubConnectionState.Reconnecting
    const isConnected = connection.state === HubConnectionState.Connected

    // Note: to keep the connection open the serverTimeout should be
    // larger than the KeepAlive value that is set on the server
    // keepAliveIntervalInMilliseconds default is 15000 and we are using default
    // serverTimeoutInMilliseconds default is 30000 and we are using 60000 set below
    connection.serverTimeoutInMilliseconds = 60000

    // re-establish the connection if connection dropped
    connection.onclose((error) => {
      console.assert(isDisconnected)
      console.log(
        'Connection closed due to error. Try refreshing this page to restart the connection',
        error
      )
      dispatch(socketStatusUpdate(connection.state))
    })

    connection.onreconnecting((error) => {
      console.assert(isReconnecting)
      console.log('Connection lost due to error. Reconnecting.', error)

      dispatch(socketStatusUpdate(connection.state))
    })

    connection.onreconnected((connectionId) => {
      console.assert(isConnected)
      console.log(
        'Connection reestablished. Connected with connectionId',
        connectionId
      )

      dispatch(socketStatusUpdate(connection.state))
    })

    startSignalRConnection(connection)

    connection.on(
      'notificationReceived',
      async (messages: ISocketMessage[]) => await messagesRecieved(messages)
    )
    connection.on('NewThread', (payload: unknown) => {
      if (isTestEnvironment) console.debug('NewThread', payload)
      if (!window.location.href.includes('kommunikasjon/meldinger')) return

      dispatch(
        handleNewThreadNotificationThunk(
          // Message payload is capitalized, need to convert to lower
          convertObjectKeysToLowercase(payload) as MessageThread
        )
      )
    })
    connection.on('NewMessage', (payload: unknown) => {
      if (isTestEnvironment) console.debug('NewMessage', payload)
      if (!window.location.href.includes('kommunikasjon/meldinger')) return

      dispatch(
        handleNewMessageNotificationThunk(
          // Message payload is capitalized, need to convert to lower
          convertObjectKeysToLowercase(payload) as DetailedMessage
        )
      )
    })

    return connection
  }

  return { initSignalR }
}

const notificationsToMarkAsRead: SocketMessageTypeEnum[] = [
  SocketMessageTypeEnum.MeetingCommentsChanged,
  SocketMessageTypeEnum.UnlocOwnerAccessCreated,
  SocketMessageTypeEnum.UnlocLockAccessCreated,
  SocketMessageTypeEnum.UnlocCreateAccessFailed,
  SocketMessageTypeEnum.InboxNewEmail,
  SocketMessageTypeEnum.ReceiptSendingStatus,
  SocketMessageTypeEnum.BoardMeetingNoticeEmailSent,
  SocketMessageTypeEnum.BoardMeetingNotification,
  SocketMessageTypeEnum.InsuranceCaseDraftTransferDone,
  SocketMessageTypeEnum.InsuranceCaseDraftTransferFailed,
]

const shouldMarkNotificationAsRead = (message: ISocketMessage) =>
  message?.id && notificationsToMarkAsRead.includes(message?.NotificationType)

export default useSignalR
